import React, { memo, useEffect, useState } from 'react';

import { MaskIcon } from '@/app/common/icons/MaskIcon';
// eslint-disable-next-line import/no-cycle
import FieldLabel from '../common/FieldLabel';
import { PlusMinusContainerStyled, PlusminusLabel, PlusMinusStyled, PlusminusSubLabel } from './styled';
import type { ControlProps, Target } from '../../index';
import { sumClasses } from '../../../../utils/misc';

const areEqual = (prevProps: PlusMinusProps, nextProps: PlusMinusProps) => {
  if (prevProps.value !== nextProps.value) return false;
  if (prevProps.maxValue !== nextProps.maxValue) return false;
  if (prevProps.minValue !== nextProps.minValue) return false;
  return true;
};

export interface PlusMinusProps extends ControlProps {
  onUpdate: (e: Target) => Promise<void>;
  maxValue: number;
  minValue: number;
  name: string;
  value: number;
  label: {
    text: string;
    subText: string;
  };
}

const Button = ({
  val,
  isPlus,
  className,
  plusMinus,
}: {
  val: string;
  isPlus?: boolean;
  className: string;
  plusMinus: (val: string) => void;
}) => (
  <button className={className} onClick={() => plusMinus(val)}>
    <MaskIcon category="Semantics" name={isPlus ? 'icon-plus' : 'icon-minus'} size={21} />
  </button>
);

const PlusMinus = memo((props: PlusMinusProps) => {
  const { className, style, label, name, disabled, onUpdate, maxValue, minValue } = props;
  const [value, setValue] = useState(props.value);

  const plusMinus = (val: string) => {
    if (disabled) return;

    let newValue = value;
    if (val === 'min') {
      if (value !== (minValue || 0)) {
        newValue = value - 1;
      }
    }

    if (val === 'plus') {
      if (value + 1 <= maxValue) {
        newValue = value + 1;
      }
    }

    setValue(newValue);
  };

  useEffect(() => {
    if (value !== props.value) {
      onUpdate({ target: { name, value } });
    }
  }, [value]);

  useEffect(() => {
    if (value !== props.value) {
      setValue(props.value);
    }
  }, [props.value]);

  return (
    <PlusMinusStyled
      className={sumClasses(['container-field plus-minus', (className && className) || ''])}
      style={style}
      disabled={disabled}
    >
      <FieldLabel name={name}>
        <PlusminusLabel>{label.text}</PlusminusLabel>
        <PlusminusSubLabel>{label.subText}</PlusminusSubLabel>
      </FieldLabel>
      <PlusMinusContainerStyled className="plus-minus-container">
        <Button
          className={sumClasses(['plus-minus-minus', value === (minValue || 0) || disabled ? 'disabled' : 'active'])}
          val="min"
          plusMinus={plusMinus}
        />
        <input type="text" name={name} id={name} value={value} disabled={disabled} readOnly={true} tabIndex={-1} />
        <Button
          className={sumClasses(['plus-minus-plus', value >= maxValue || disabled ? 'disabled' : 'active'])}
          val="plus"
          plusMinus={plusMinus}
          isPlus={true}
        />
      </PlusMinusContainerStyled>
    </PlusMinusStyled>
  );
}, areEqual);

PlusMinus.displayName = 'PlusMinus';

export default PlusMinus;
